import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
  ArrowLeft, Plus, Search, Filter, Trash2, Copy, BookOpen,
  Atom, FlaskConical, Calculator, Dna, CheckCircle, Hash, ListChecks,
  Database, Upload, FileSpreadsheet, X
} from 'lucide-react';
import { toast } from 'sonner';
import ThemeToggle from '../components/ThemeToggle';

const EXAM_CONFIG = {
  'JEE Mains': {
    subjects: ['Physics', 'Chemistry', 'Mathematics'],
    question_types: ['MCQ', 'Numerical'],
    color: 'from-blue-500 to-cyan-500',
    bgColor: 'bg-blue-500/10'
  },
  'JEE Advanced': {
    subjects: ['Physics', 'Chemistry', 'Mathematics'],
    question_types: ['MCQ', 'Numerical', 'Multiple Choice'],
    color: 'from-purple-500 to-pink-500',
    bgColor: 'bg-purple-500/10'
  },
  'NEET': {
    subjects: ['Physics', 'Chemistry', 'Biology'],
    question_types: ['MCQ'],
    color: 'from-green-500 to-emerald-500',
    bgColor: 'bg-green-500/10'
  }
};

const SUBJECT_ICONS = {
  'Physics': Atom,
  'Chemistry': FlaskConical,
  'Mathematics': Calculator,
  'Biology': Dna
};

export default function QuestionBank() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [stats, setStats] = useState(null);
  const [testSeries, setTestSeries] = useState([]);
  
  // Filters
  const [filterExam, setFilterExam] = useState('');
  const [filterSubject, setFilterSubject] = useState('');
  const [filterDifficulty, setFilterDifficulty] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Dialogs
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [showAddToTest, setShowAddToTest] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    question_text: '',
    question_type: 'MCQ',
    options: ['', '', '', ''],
    correct_answer: '',
    exam_type: 'JEE Mains',
    subject: 'Physics',
    topic: '',
    difficulty: 'Medium',
    tags: []
  });
  
  // Bulk upload
  const [bulkData, setBulkData] = useState('');
  const [bulkTestId, setBulkTestId] = useState('');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterExam) params.append('exam_type', filterExam);
      if (filterSubject) params.append('subject', filterSubject);
      if (filterDifficulty) params.append('difficulty', filterDifficulty);
      
      const [questionsRes, statsRes, testsRes] = await Promise.all([
        api.get(`/admin/question-bank?${params.toString()}`),
        api.get('/admin/question-bank/stats'),
        api.get('/admin/test-series-stats')
      ]);
      
      setQuestions(questionsRes.data);
      setStats(statsRes.data);
      setTestSeries(testsRes.data);
    } catch (error) {
      toast.error('Failed to load question bank');
    } finally {
      setLoading(false);
    }
  }, [filterExam, filterSubject, filterDifficulty]);

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      toast.error('Admin access required');
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [user, navigate, fetchData]);

  const handleAddQuestion = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        options: formData.question_type === 'Numerical' ? null : formData.options.filter(o => o.trim())
      };
      await api.post('/admin/question-bank', payload);
      toast.success('Question added to bank!');
      setShowAddDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to add question');
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!window.confirm('Delete this question from the bank?')) return;
    try {
      await api.delete(`/admin/question-bank/${questionId}`);
      toast.success('Question deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleAddToTest = async (testId) => {
    if (!selectedQuestion) return;
    try {
      await api.post(`/admin/question-bank/add-to-test?question_id=${selectedQuestion.id}&test_series_id=${testId}`);
      toast.success('Question added to test!');
      setShowAddToTest(false);
      setSelectedQuestion(null);
    } catch (error) {
      toast.error('Failed to add to test');
    }
  };

  const handleBulkUpload = async () => {
    if (!bulkTestId || !bulkData.trim()) {
      toast.error('Please select a test and paste question data');
      return;
    }
    
    try {
      // Parse CSV/JSON data
      const lines = bulkData.trim().split('\n');
      const questions = [];
      
      for (const line of lines) {
        if (!line.trim()) continue;
        
        // Try to parse as JSON first
        try {
          const parsed = JSON.parse(line);
          questions.push(parsed);
        } catch {
          // Parse as CSV: question_text|option1|option2|option3|option4|correct_answer
          const parts = line.split('|').map(p => p.trim());
          if (parts.length >= 6) {
            questions.push({
              question_text: parts[0],
              question_type: 'MCQ',
              options: [parts[1], parts[2], parts[3], parts[4]],
              correct_answer: parts[5]
            });
          }
        }
      }
      
      if (questions.length === 0) {
        toast.error('No valid questions found');
        return;
      }
      
      const response = await api.post('/admin/bulk-upload-questions', {
        test_series_id: bulkTestId,
        questions
      });
      
      toast.success(`Added ${response.data.added_count} questions!`);
      if (response.data.errors.length > 0) {
        toast.warning(`${response.data.errors.length} errors occurred`);
      }
      
      setShowBulkUpload(false);
      setBulkData('');
      setBulkTestId('');
    } catch (error) {
      toast.error('Failed to upload questions');
    }
  };

  const resetForm = () => {
    setFormData({
      question_text: '',
      question_type: 'MCQ',
      options: ['', '', '', ''],
      correct_answer: '',
      exam_type: 'JEE Mains',
      subject: 'Physics',
      topic: '',
      difficulty: 'Medium',
      tags: []
    });
  };

  const filteredQuestions = questions.filter(q => 
    !searchQuery || q.question_text.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading question bank...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="question-bank-page">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/admin')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Admin
            </Button>
            <div className="h-6 w-px bg-border"></div>
            <span className="font-semibold text-primary flex items-center gap-2">
              <Database className="w-5 h-5" />
              Question Bank
            </span>
          </div>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <Button variant="outline" onClick={() => setShowBulkUpload(true)}>
              <Upload className="w-4 h-4 mr-2" />
              Bulk Upload
            </Button>
            <Button onClick={() => setShowAddDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add Question
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-br from-primary to-secondary rounded-2xl p-5 text-white"
          >
            <Database className="w-8 h-8 mb-2 opacity-80" />
            <div className="text-3xl font-bold font-mono">{stats?.total || 0}</div>
            <div className="text-sm opacity-80">Total Questions</div>
          </motion.div>
          
          {Object.entries(EXAM_CONFIG).map(([exam, config], index) => {
            const examStats = stats?.by_exam_and_subject?.[exam] || {};
            const total = Object.values(examStats).reduce((sum, count) => sum + count, 0);
            
            return (
              <motion.div
                key={exam}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * (index + 1) }}
                className={`bg-gradient-to-br ${config.color} rounded-2xl p-5 text-white`}
              >
                <BookOpen className="w-8 h-8 mb-2 opacity-80" />
                <div className="text-3xl font-bold font-mono">{total}</div>
                <div className="text-sm opacity-80">{exam}</div>
              </motion.div>
            );
          })}
        </div>

        {/* Filters */}
        <div className="bg-card border border-border rounded-2xl p-4 mb-6">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search questions..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            <Select value={filterExam || "all"} onValueChange={(v) => setFilterExam(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Exam Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Exams</SelectItem>
                {Object.keys(EXAM_CONFIG).map(exam => (
                  <SelectItem key={exam} value={exam}>{exam}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={filterSubject || "all"} onValueChange={(v) => setFilterSubject(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Subject" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Subjects</SelectItem>
                <SelectItem value="Physics">Physics</SelectItem>
                <SelectItem value="Chemistry">Chemistry</SelectItem>
                <SelectItem value="Mathematics">Mathematics</SelectItem>
                <SelectItem value="Biology">Biology</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={filterDifficulty || "all"} onValueChange={(v) => setFilterDifficulty(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[130px]">
                <SelectValue placeholder="Difficulty" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="Easy">Easy</SelectItem>
                <SelectItem value="Medium">Medium</SelectItem>
                <SelectItem value="Hard">Hard</SelectItem>
              </SelectContent>
            </Select>
            
            {(filterExam || filterSubject || filterDifficulty) && (
              <Button variant="ghost" size="sm" onClick={() => {
                setFilterExam('');
                setFilterSubject('');
                setFilterDifficulty('');
              }}>
                <X className="w-4 h-4 mr-1" />
                Clear
              </Button>
            )}
          </div>
        </div>

        {/* Questions List */}
        <div className="space-y-4">
          {filteredQuestions.length === 0 ? (
            <div className="bg-card border-2 border-dashed border-border rounded-2xl p-12 text-center">
              <Database className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">No questions found</h3>
              <p className="text-muted-foreground mb-4">
                {questions.length === 0 
                  ? "Start building your question bank by adding questions"
                  : "Try adjusting your filters"}
              </p>
              <Button onClick={() => setShowAddDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Add First Question
              </Button>
            </div>
          ) : (
            filteredQuestions.map((question, index) => {
              const SubjectIcon = SUBJECT_ICONS[question.subject] || BookOpen;
              const config = EXAM_CONFIG[question.exam_type] || {};
              
              return (
                <motion.div
                  key={question.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.03 }}
                  className="bg-card border border-border rounded-xl p-5 hover:border-primary/30 transition-colors"
                >
                  <div className="flex gap-4">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${config.color || 'from-gray-500 to-gray-600'} flex items-center justify-center text-white shrink-0`}>
                      <SubjectIcon className="w-6 h-6" />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap gap-2 mb-2">
                        <span className={`text-xs px-2 py-1 rounded-full ${config.bgColor || 'bg-muted'}`}>
                          {question.exam_type}
                        </span>
                        <span className="text-xs px-2 py-1 rounded-full bg-muted">
                          {question.subject}
                        </span>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          question.difficulty === 'Easy' ? 'bg-success/10 text-success' :
                          question.difficulty === 'Hard' ? 'bg-destructive/10 text-destructive' :
                          'bg-warning/10 text-warning'
                        }`}>
                          {question.difficulty}
                        </span>
                        <span className="text-xs px-2 py-1 rounded-full bg-muted">
                          {question.question_type}
                        </span>
                      </div>
                      
                      <p className="text-foreground mb-3 line-clamp-2">{question.question_text}</p>
                      
                      {question.options && question.options.length > 0 && (
                        <div className="flex flex-wrap gap-2 text-sm text-muted-foreground">
                          {question.options.map((opt, i) => (
                            <span 
                              key={i} 
                              className={`px-2 py-0.5 rounded ${
                                opt === question.correct_answer ? 'bg-success/10 text-success' : 'bg-muted'
                              }`}
                            >
                              {String.fromCharCode(65 + i)}. {opt.substring(0, 30)}{opt.length > 30 ? '...' : ''}
                            </span>
                          ))}
                        </div>
                      )}
                      
                      {question.used_in_tests?.length > 0 && (
                        <p className="text-xs text-muted-foreground mt-2">
                          Used in {question.used_in_tests.length} test(s)
                        </p>
                      )}
                    </div>
                    
                    <div className="flex flex-col gap-2 shrink-0">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => { setSelectedQuestion(question); setShowAddToTest(true); }}
                      >
                        <Copy className="w-4 h-4 mr-1" />
                        Add to Test
                      </Button>
                      <Button 
                        size="sm" 
                        variant="destructive"
                        onClick={() => handleDeleteQuestion(question.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </motion.div>
              );
            })
          )}
        </div>
      </main>

      {/* Add Question Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-outfit">Add to Question Bank</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleAddQuestion} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Exam Type</Label>
                <Select 
                  value={formData.exam_type} 
                  onValueChange={(v) => {
                    setFormData(prev => ({ 
                      ...prev, 
                      exam_type: v,
                      subject: EXAM_CONFIG[v]?.subjects[0] || 'Physics'
                    }));
                  }}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.keys(EXAM_CONFIG).map(exam => (
                      <SelectItem key={exam} value={exam}>{exam}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Subject</Label>
                <Select 
                  value={formData.subject} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, subject: v }))}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(EXAM_CONFIG[formData.exam_type]?.subjects || []).map(subj => (
                      <SelectItem key={subj} value={subj}>{subj}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Question Type</Label>
                <Select 
                  value={formData.question_type} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, question_type: v }))}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(EXAM_CONFIG[formData.exam_type]?.question_types || ['MCQ']).map(qt => (
                      <SelectItem key={qt} value={qt}>{qt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Difficulty</Label>
                <Select 
                  value={formData.difficulty} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, difficulty: v }))}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Easy">Easy</SelectItem>
                    <SelectItem value="Medium">Medium</SelectItem>
                    <SelectItem value="Hard">Hard</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Question Text *</Label>
              <Textarea
                value={formData.question_text}
                onChange={(e) => setFormData(prev => ({ ...prev, question_text: e.target.value }))}
                placeholder="Enter your question..."
                rows={3}
                required
              />
            </div>
            
            {formData.question_type !== 'Numerical' && (
              <div className="space-y-2">
                <Label>Options</Label>
                <div className="grid grid-cols-2 gap-2">
                  {formData.options.map((opt, i) => (
                    <Input
                      key={i}
                      value={opt}
                      onChange={(e) => {
                        const newOptions = [...formData.options];
                        newOptions[i] = e.target.value;
                        setFormData(prev => ({ ...prev, options: newOptions }));
                      }}
                      placeholder={`Option ${String.fromCharCode(65 + i)}`}
                    />
                  ))}
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Correct Answer *</Label>
              <Input
                value={formData.correct_answer}
                onChange={(e) => setFormData(prev => ({ ...prev, correct_answer: e.target.value }))}
                placeholder={formData.question_type === 'Numerical' ? 'Enter numeric answer' : 'Enter the correct option text'}
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label>Topic (optional)</Label>
              <Input
                value={formData.topic}
                onChange={(e) => setFormData(prev => ({ ...prev, topic: e.target.value }))}
                placeholder="e.g., Thermodynamics, Organic Chemistry"
              />
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button type="submit" className="flex-1">Add to Bank</Button>
              <Button type="button" variant="outline" onClick={() => { setShowAddDialog(false); resetForm(); }}>
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add to Test Dialog */}
      <Dialog open={showAddToTest} onOpenChange={setShowAddToTest}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Question to Test</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Select a test series to add this question to:
            </p>
            
            <div className="max-h-[300px] overflow-y-auto space-y-2">
              {testSeries.map(test => (
                <div
                  key={test.id}
                  className="p-3 border border-border rounded-lg hover:border-primary/50 cursor-pointer transition-colors"
                  onClick={() => handleAddToTest(test.id)}
                >
                  <div className="font-medium">{test.title}</div>
                  <div className="text-sm text-muted-foreground">
                    {test.exam_type || test.category} • {test.subject || 'General'} • {test.questions_count} questions
                  </div>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Upload Dialog */}
      <Dialog open={showBulkUpload} onOpenChange={setShowBulkUpload}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5" />
              Bulk Upload Questions
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Select Test Series *</Label>
              <Select value={bulkTestId} onValueChange={setBulkTestId}>
                <SelectTrigger><SelectValue placeholder="Choose a test..." /></SelectTrigger>
                <SelectContent>
                  {testSeries.map(test => (
                    <SelectItem key={test.id} value={test.id}>
                      {test.title} ({test.exam_type || test.category})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Question Data *</Label>
              <Textarea
                value={bulkData}
                onChange={(e) => setBulkData(e.target.value)}
                placeholder={`Paste questions in this format (one per line):
Question text|Option A|Option B|Option C|Option D|Correct Answer

Example:
What is 2+2?|1|2|3|4|4
Capital of India?|Mumbai|Delhi|Chennai|Kolkata|Delhi`}
                rows={10}
                className="font-mono text-sm"
              />
            </div>
            
            <div className="bg-muted/50 rounded-lg p-4 text-sm">
              <p className="font-semibold mb-2">Format Guide:</p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                <li>Use pipe (|) to separate fields</li>
                <li>Format: question|optA|optB|optC|optD|correct_answer</li>
                <li>One question per line</li>
                <li>Correct answer should match one of the options exactly</li>
              </ul>
            </div>
            
            <div className="flex gap-3">
              <Button onClick={handleBulkUpload} className="flex-1">
                <Upload className="w-4 h-4 mr-2" />
                Upload Questions
              </Button>
              <Button variant="outline" onClick={() => { setShowBulkUpload(false); setBulkData(''); setBulkTestId(''); }}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
